import { useContext } from 'react'
import { create } from 'zustand'
import { useShallow } from 'zustand/shallow'
import useNotificationStore from './useNotificationStore'
import { GlobalContext } from '../context/GlobalContext'

let globalNotify = null
let globalApir = null
let globalToken = null

const useLockStore = create((set, get) => {
  const pendingRequests = new Map()
  let reconnectAttempts = 0
  let maxReconnectAttempts = 5
  let reconnectTimeout = null
  let connectionStartTime = null
  let lastForceReconnectTime = 0

  return {
    socket: null,
    connected: false,
    locks: {},
    error: null,
    reconnectAttempts: 0,
    lastConnectionTime: null,
    // Wait for websocket connection to be ready before sending lock/unlock
    // Resolves when connected or rejects after timeout
    _waitForConnection: (timeoutMs = 3000) =>
      new Promise((resolve, reject) => {
        const { socket, connected } = get()
        if (socket?.readyState === WebSocket.OPEN && connected) {
          resolve()
          return
        }

        const start = Date.now()
        const intervalId = setInterval(() => {
          const { socket: s, connected: c } = get()
          if (s?.readyState === WebSocket.OPEN && c) {
            clearInterval(intervalId)
            resolve()
          } else if (Date.now() - start >= timeoutMs) {
            clearInterval(intervalId)
            reject(new Error('Not connected to server'))
          }
        }, 50)
      }),

    _attemptReconnect: (token, notifyFn, apir) => {
      // Don't block reconnection attempts - keep trying indefinitely
      reconnectAttempts++

      // Use exponential backoff, but cap at 30s max delay
      // After maxReconnectAttempts, continue with the max delay
      const attemptIndex = Math.min(reconnectAttempts - 1, maxReconnectAttempts - 1)
      const delay = Math.min(1000 * Math.pow(2, attemptIndex), 30000)

      // Show attempt number, cycling back to 1 after maxReconnectAttempts
      const displayAttempt = ((reconnectAttempts - 1) % maxReconnectAttempts) + 1
      console.log(`🔄 Attempting reconnection ${displayAttempt}/${maxReconnectAttempts} in ${delay}ms (total attempts: ${reconnectAttempts})`)
      set({ reconnectAttempts: displayAttempt, error: `Reconnecting... (${displayAttempt}/${maxReconnectAttempts})` })

      reconnectTimeout = setTimeout(() => {
        get().connect(token, notifyFn, apir, true)
      }, delay)
    },

    connect: (token, notifyFn, apir, isReconnect = false) => {
      globalNotify = notifyFn
      globalApir = apir
      globalToken = token

      console.info(`🔌 WebSocket connection attempt to: ${apir}`)
      console.info(`📊 Reconnect attempt: ${isReconnect ? 'Yes' : 'No'}, Attempt #${reconnectAttempts}`)

      // Clear any existing reconnection timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }

      // Close existing connection if any
      const { socket: existingSocket } = get()
      if (existingSocket && existingSocket.readyState === WebSocket.OPEN) {
        console.warn('⚠️ Closing existing WebSocket connection')
        existingSocket.close()
      }

      if (!isReconnect && get().socket?.readyState === WebSocket.OPEN) {
        console.warn('⚠️ WebSocket connection already exists')
        return
      }

      try {
        connectionStartTime = Date.now()
        const socket = new WebSocket(
          `wss://${apir}/lockSocket?token=${encodeURIComponent(token)}`
        )

        socket.onopen = () => {
          const connectionTime = Date.now() - connectionStartTime
          console.log(`✅ WebSocket connected successfully in ${connectionTime}ms`)
          reconnectAttempts = 0 // Reset on successful connection
          set({
            socket,
            connected: true,
            error: null,
            reconnectAttempts: 0,
            lastConnectionTime: new Date().toISOString()
          })
        }

        socket.onclose = (event) => {
          const connectionDuration = connectionStartTime ? Date.now() - connectionStartTime : 0
          console.log(`🔌 WebSocket closed after ${connectionDuration}ms. Code: ${event.code}, Reason: ${event.reason || 'Unknown'}`)

          pendingRequests.forEach((pending) => {
            pending.reject(new Error(`Connection closed: ${event.reason || 'Unknown reason'}`))
          })

          pendingRequests.clear()

          set({
            socket: null,
            connected: false,
            error: `Connection closed: ${event.reason || 'Unknown reason'}`
          })

          // Attempt reconnection if not a clean close
          // Always attempt reconnection regardless of isReconnect flag
          // The _attemptReconnect function handles attempt limiting and exponential backoff
          if (event.code !== 1000) {
            get()._attemptReconnect(token, notifyFn, apir)
          }
        }

        socket.onerror = (error) => {
          console.error('❌ WebSocket error:', error)
          console.error('❌ WebSocket readyState:', socket.readyState)

          // Check if this is a navigation-related error (message port closed)
          if (error.type === 'error' && socket.readyState === WebSocket.CLOSED) {
            console.warn('🚨 Navigation or auth error detected - connection interrupted')
            // Don't set error state immediately, this is likely due to navigation/auth issues
            set({
              connected: false,
              error: 'Connection interrupted - likely due to authentication or navigation'
            })
          } else {
            set({ error: 'WebSocket error occurred' })
          }
        }

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            console.log('📡 SOCKET MESSAGE received at:', new Date().toISOString(), message)

            switch (message.type) {
              case 'lockState':
                set({ locks: message.data })
                console.log('🔒 Lock state updated:', message.data)

                // Resolve any pending requests based on current lock map
                try {
                  pendingRequests.forEach((pending, key) => {
                    const enqId = key
                    const isLockedNow = !!message.data?.[enqId]
                    console.log(`🔍 Checking pending ${pending.action} for ${enqId}: locked=${isLockedNow}`)
                    if (pending.action === 'lock' && isLockedNow) {
                      console.log(`✅ Resolving lock request for ${enqId} based on lockState`)
                      clearTimeout(pending.timeoutId)
                      pending.resolve(true)
                      pendingRequests.delete(enqId)
                    } else if (pending.action === 'unlock' && !isLockedNow) {
                      console.log(`✅ Resolving unlock request for ${enqId} based on lockState`)
                      clearTimeout(pending.timeoutId)
                      pending.resolve(true)
                      pendingRequests.delete(enqId)
                    }
                  })
                } catch (e) {
                  console.warn('Pending resolution error:', e)
                }
                break

              case 'lockResponse':
              case 'unlockResponse': {
                const key = String(message.id)
                const pending = pendingRequests.get(key)
                console.log(`📨 Received ${message.type} for ${key}: success=${message.success}`)
                if (pending) {
                  clearTimeout(pending.timeoutId)
                  if (message.success) {
                    console.log(`✅ Resolving ${pending.action} request for ${key}`)
                    pending.resolve(true)
                  } else {
                    console.log(`❌ Rejecting ${pending.action} request for ${key}: ${message.message}`)
                    pending.reject(new Error(`Lock operation failed: ${message.message || 'Unknown error'}`))
                  }
                  pendingRequests.delete(key)
                } else {
                  console.warn(`⚠️ Received ${message.type} for ${key} but no pending request found`)
                }
                break
              }

              case 'notification': {
                const notif = message.data
                console.log('🔔 Notification received at:', new Date().toISOString(), 'Type:', notif.type, 'Data:', notif)
                if (globalNotify) {
                  // Show notification immediately when websocket message is received
                  if (notif.type === 'statusChange') {
                    console.log('📢 Showing status change notification')
                    globalNotify(
                      'Enquiry updated',
                      `Enquiry ${notif.enquiry.Document} status changed from ${notif.oldValue} to ${notif.newValue}`
                    )
                  } else if (notif.type === 'unreadIncrease') {
                    console.log('📢 Showing unread message notification')
                    globalNotify('Message received', `Enquiry ${notif.enquiry.Document} has a new message`)
                  } else if (notif.type === 'newEnquiry') {
                    console.log('📢 Showing new enquiry notification')
                    globalNotify('New enquiry', `Enquiry ${notif.enquiry.Document} has been received`)
                  }
                } else {
                  console.warn('⚠️ globalNotify function not available')
                }
                break
              }

              case 'error':
                set({ error: message.message })

                pendingRequests.forEach((pending) => {
                  clearTimeout(pending.timeoutId)
                  pending.reject(new Error(message.message))
                })

                pendingRequests.clear()
                break

              default:
                console.warn('Unknown message type:', message.type)
            }
          } catch (error) {
            console.error('Error processing message:', error)
            set({ error: 'Error processing server message' })
          }
        }

        console.log('Successful connection to websocket')
        set({ socket })
      } catch (error) {
        console.error('Connection error:', error)
        set({ error: 'Failed to establish connection' })
      }
    },

    disconnect: () => {
      const { socket } = get()

      console.log('🔌 Manually disconnecting WebSocket')

      // Clear reconnection timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }

      if (socket) {
        pendingRequests.forEach((pending) => {
          clearTimeout(pending.timeoutId)
          pending.reject(new Error('Disconnected'))
        })

        pendingRequests.clear()
        socket.close(1000, 'Manual disconnect') // Clean close
        set({
          socket: null,
          connected: false,
          locks: {},
          reconnectAttempts: 0,
          error: null
        })
      }
    },

    // Force reconnection - useful when connection gets stuck
    forceReconnect: () => {
      console.log('🔄 Force reconnection requested')
      const { socket } = get()
      const now = Date.now()

      // Prevent multiple simultaneous reconnection attempts
      if (reconnectTimeout) {
        console.log('🔄 Reconnection already in progress, skipping...')
        return
      }

      // Debounce rapid force reconnect attempts (min 2 seconds between attempts)
      if (now - lastForceReconnectTime < 2000) {
        console.log('🔄 Force reconnect debounced - too soon since last attempt')
        return
      }
      lastForceReconnectTime = now

      // Close existing connection
      if (socket) {
        socket.close(1000, 'Force reconnect')
      }

      // Reset state
      set({
        socket: null,
        connected: false,
        locks: {},
        reconnectAttempts: 0,
        error: 'Reconnecting...'
      })

      // Attempt reconnection if we have the required data
      if (globalNotify && globalApir && globalToken) {
        console.log('🔄 Attempting force reconnection with stored credentials...')
        // Reset reconnect attempts for force reconnect
        reconnectAttempts = 0
        get().connect(globalToken, globalNotify, globalApir, true)
      } else {
        console.warn('⚠️ Cannot force reconnect - missing credentials:', {
          hasNotify: !!globalNotify,
          hasApir: !!globalApir,
          hasToken: !!globalToken
        })
        set({ error: 'Cannot reconnect - missing credentials' })
      }
    },

    // Health check function to monitor connection
    checkConnectionHealth: () => {
      const { socket, connected } = get()
      const now = Date.now()

      if (!socket || !connected) {
        console.log('🔍 Connection health check: Not connected')
        // Don't trigger reconnection from health check - let the UI handle it
        return false
      }

      if (socket.readyState !== WebSocket.OPEN) {
        console.log(`🔍 Connection health check: Socket state is ${socket.readyState}`)
        // Don't trigger reconnection from health check - let the UI handle it
        return false
      }

      console.log('🔍 Connection health check: Healthy')
      return true
    },

    lockEnq: (id) => {
      const waitForConnection = get()._waitForConnection
      const requestKey = String(id)

      // Check if there's already a pending lock request for this enquiry
      if (pendingRequests.has(requestKey)) {
        console.log(`🔄 Lock request already pending for ${id}, returning existing promise`)
        return pendingRequests.get(requestKey).promise
      }

      return waitForConnection().then(() => {
        const { socket } = get()

        let resolve, reject, timeoutId
        const promise = new Promise((res, rej) => {
          resolve = res
          reject = rej

          timeoutId = setTimeout(() => {
            if (pendingRequests.has(requestKey)) {
              console.warn(`⏰ Lock request timeout for ${id} after 3 seconds`)
              pendingRequests.delete(requestKey)
              reject(new Error('Lock request timeout'))
            }
          }, 3000) // Reduced to 3 seconds for faster feedback

          console.log(`🔒 Sending lock request for ${id}`)

          socket.send(
            JSON.stringify({
              type: 'lock',
              id: String(id)
            })
          )
        })

        // Store the promise and its handlers after they're created
        pendingRequests.set(requestKey, {
          resolve,
          reject,
          timeoutId,
          action: 'lock',
          promise
        })

        return promise
      })
    },

    unlockEnq: (id) => {
      const waitForConnection = get()._waitForConnection
      const requestKey = String(id)

      // Check if there's already a pending unlock request for this enquiry
      if (pendingRequests.has(requestKey)) {
        console.log(`🔄 Unlock request already pending for ${id}, returning existing promise`)
        return pendingRequests.get(requestKey).promise
      }

      return waitForConnection().then(() => {
        const { socket } = get()

        let resolve, reject, timeoutId
        const promise = new Promise((res, rej) => {
          resolve = res
          reject = rej

          timeoutId = setTimeout(() => {
            if (pendingRequests.has(requestKey)) {
              console.warn(`⏰ Unlock request timeout for ${id} after 3 seconds`)
              pendingRequests.delete(requestKey)
              reject(new Error('Unlock request timeout'))
            }
          }, 3000) // Reduced to 3 seconds for faster feedback

          console.log(`🔓 Sending unlock request for ${id}`)

          socket.send(
            JSON.stringify({
              type: 'unlock',
              id: String(id)
            })
          )
        })

        // Store the promise and its handlers after they're created
        pendingRequests.set(requestKey, {
          resolve,
          reject,
          timeoutId,
          action: 'unlock',
          promise
        })

        return promise
      })
    },

    clearError: () => set({ error: null })
  }
})

export const useEnquiryLock = (enqId) => {
  const lockState = useLockStore(useShallow((state) => state.locks[enqId]))
  const connected = useLockStore((state) => state.connected)
  const error = useLockStore((state) => state.error)
  const lockEnq = useLockStore((state) => state.lockEnq)
  const unlockEnq = useLockStore((state) => state.unlockEnq)

  return {
    lockState,
    connected,
    error,
    lock: () => lockEnq(enqId),
    unlock: () => unlockEnq(enqId)
  }
}

export const useLockConnection = () => {
  const { notify } = useNotificationStore()
  const { cinfo, apir } = useContext(GlobalContext)
  const connect = useLockStore((state) => state.connect)
  const disconnect = useLockStore((state) => state.disconnect)
  const clearError = useLockStore((state) => state.clearError)

  return {
    connect: (token) => connect(token, notify, apir),
    disconnect,
    clearError
  }
}

export default useLockStore
